import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'
import { ICalendarDates } from '~/client/src/shared/models/CalendarEvent'
import Workflow from '~/client/src/shared/models/Workflow'
import {
  claimKeyboard,
  KeyboardClaim,
  releaseKeyboard,
} from '~/client/src/shared/utils/keyboardClaim'

import SitePermitCreationForm from './SitePermitCreationForm'
import WorkflowDraftForm from './WorkflowDraftForm'
import SitePermitViewForm from './WorkflowViewForm'

interface IProps {
  workflowToShow: Workflow
  FileInputType

  isMobileMode?: boolean
  calendarEventDates?: ICalendarDates
  isDateMoveConfirmModalOpen?: boolean

  onClose(): void
  openFilePreview?: (
    files: IFilePreviewProperties[],
    currentIndex: number,
  ) => void
  hideDateMoveConfirmModal?: () => void
  openWorkflow?: (workflow: Workflow) => void
}

@observer
export default class WorkflowViewFormWrapper extends React.Component<IProps> {
  @observable private workflowToDuplicate: Workflow = null
  private keyboardClaim: KeyboardClaim = null

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  // The sidebar covers the map, so it — not the surface underneath — owns the
  // keyboard: markup tool hotkeys must not fire while the form is open.
  public componentDidMount() {
    this.keyboardClaim = claimKeyboard()
  }

  public componentWillUnmount() {
    releaseKeyboard(this.keyboardClaim)
    this.keyboardClaim = null
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { workflowToShow } = this.props

    if (workflowToShow !== prevProps.workflowToShow) {
      this.unsetDuplicateWorkflow()
    }
  }

  public render() {
    const {
      workflowToShow,
      isMobileMode,
      calendarEventDates,
      isDateMoveConfirmModalOpen,
      FileInputType,
      openFilePreview,
      hideDateMoveConfirmModal,
      onClose,
      openWorkflow,
    } = this.props

    if (this.workflowToDuplicate) {
      return (
        <SitePermitCreationForm
          workflowToDuplicate={this.workflowToDuplicate}
          isTypeReadOnly
          FileInputType={FileInputType}
          close={onClose}
          openWorkflow={openWorkflow}
        />
      )
    }

    if (workflowToShow.isDraft) {
      return (
        <WorkflowDraftForm
          draftWorkflow={workflowToShow}
          FileInputType={FileInputType}
          close={onClose}
          onDuplicateClick={this.setDuplicateWorkflow}
          openWorkflow={openWorkflow}
        />
      )
    }

    return (
      <SitePermitViewForm
        permitToShow={workflowToShow}
        isMobileMode={isMobileMode}
        calendarEventDates={calendarEventDates}
        isDateMoveConfirmModalOpen={isDateMoveConfirmModalOpen}
        FileInputType={FileInputType}
        close={onClose}
        openFilePreview={openFilePreview}
        hideDateMoveConfirmModal={hideDateMoveConfirmModal}
        onDuplicateClick={this.setDuplicateWorkflow}
        openWorkflow={openWorkflow}
      />
    )
  }

  @action.bound
  private setDuplicateWorkflow(workflow: Workflow) {
    this.workflowToDuplicate = workflow
  }

  @action.bound
  private unsetDuplicateWorkflow() {
    this.workflowToDuplicate = null
  }
}
